import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useMemo, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import useReportTableContactDetails from "hooks/organization/reports/types/useReportTableContactDetails";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import CompanyOptions from "modules/companies/CompanyOptions";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProposalSchema } from "DDD/action-objects/ProposalCreate";
import { z } from "zod";

// Extended schema for company filter
const reportFilterSchema = createProposalSchema.extend({
  companyId: z.string().optional(),
});

type ReportFilterFormData = z.infer<typeof reportFilterSchema>;

// Type for contact details variables
type ContactDetailsVariables = {
  companyId?: string;
};

const getReportDefaultValues = (
  initialValues: Partial<ReportFilterFormData>
): Partial<ReportFilterFormData> => ({
  companyId: "",
  source: undefined,
  ...initialValues,
});

const ReportContactDetails = () => {
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [generateReport, { data: generateReportData }] = useGenerateReport();

  const [companyName, setCompanyName] = useState("");
  const [triggerRefetch, setTriggerRefetch] = useState(false);
  const [getCompany, { company }] = useGetCompanyLazy();
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    id: true,
    name: true,
    phone: true,
    email: true,
    first_name: true,
    last_name: true,
    contact_email: true,
    contact_phone: true,
    address_line_1: false,
    address_line_2: false,
    address_city: false,
    address_state: false,
    address_zip: false,
    address_country: false,
    website: false,
    notes: false,
    status: false,
    title: false,
    role: false,
    company_id: false,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.id,
      },
      {
        Header: "Company Name",
        accessor: "name",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.name,
      },
      {
        Header: "Phone",
        accessor: "phone",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.phone,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.email,
      },
      {
        Header: "Contact First Name",
        accessor: "first_name",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.first_name,
      },
      {
        Header: "Contact Last Name",
        accessor: "last_name",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.last_name,
      },
      {
        Header: "Contact Email",
        accessor: "contact_email",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.contact_email,
      },
      {
        Header: "Contact Phone",
        accessor: "contact_phone",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.contact_phone,
      },
      // Hidden columns by default
      {
        Header: "Address Line 1",
        accessor: "address_line_1",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_line_1,
      },
      {
        Header: "Address Line 2",
        accessor: "address_line_2",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_line_2,
      },
      {
        Header: "City",
        accessor: "address_city",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_city,
      },
      {
        Header: "State",
        accessor: "address_state",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_state,
      },
      {
        Header: "Zip",
        accessor: "address_zip",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_zip,
      },
      {
        Header: "Country",
        accessor: "address_country",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.address_country,
      },
      {
        Header: "Website",
        accessor: "website",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.website,
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ value }) => <div dangerouslySetInnerHTML={{ __html: value || "" }} />,
        disableSortBy: true,
        visible: columnVisibility.notes,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.status,
      },
      {
        Header: "Contact Title",
        accessor: "title",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.title,
      },
      {
        Header: "Contact Role",
        accessor: "role",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.role,
      },
      {
        Header: "Company ID",
        accessor: "company_id",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
        visible: columnVisibility.company_id,
      },
    ];
  }, [columnVisibility]);

  const companyInitialId = null;

  const methods = useForm<ReportFilterFormData>({
    resolver: zodResolver(reportFilterSchema),
    defaultValues: getReportDefaultValues({ companyId: companyInitialId }),
  });

  const companyId = methods.watch("companyId");

  const { tableData, loading, pagination, getReportContactDetails } =
    useReportTableContactDetails(companyId);

  const [requestData, setRequestData] = useState<ContactDetailsVariables | null>(null);

  const downloadFileRef = useRef(null);

  useEffect(() => {
    if (generateReportData?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(
        ReportType.CONTACT_DETAILS
      )}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData]); // Remove requestData dependency to prevent re-downloads on filter

  const onSubmit = (values) => {
    const variables = {
      companyId,
    };
    setRequestData({ companyId });
    getReportContactDetails({ page: 1, first: 25 });
  };

  const handleDownload = (e) => {
    const reportVariables: GenerateReportMutationVariables = {
      type: ReportType.CONTACT_DETAILS,
      companyId,
    };
    generateReport({ variables: reportVariables });
  };

  // Initial data load when component mounts
  useEffect(() => {
    const variables = {
      companyId,
    };
    setRequestData({ companyId });
    getReportContactDetails({ page: 1, first: 25 });
  }, []); // Only run once on mount

  useEffect(() => {
    if (companyId) {
      getCompany({ id: companyId });
    }
  }, [getCompany, companyId]);

  // Sync companyName with selected company
  useEffect(() => {
    if (company?.name) {
      setCompanyName(company.name);
    } else if (companyId === null || companyId === undefined) {
      setCompanyName("");
    }
  }, [company, companyId]);

  const handleColumnVisibilityChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Create filtered table data with visible columns
  const filteredTableData = useMemo(() => {
    if (!tableData) return null;

    return {
      columns: columns.filter((column) => column.visible),
      rows: tableData.rows,
      modal: tableData.modal,
    };
  }, [tableData, columns]);

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <FormProvider {...methods}>
          <MDBox mb={6} component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card>
                  <MDBox
                    p={3}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h4" fontWeight="medium">
                        {capitalCase(ReportType.CONTACT_DETAILS)} Report
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={1}>
                        View comprehensive contact information and activity
                      </MDTypography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>contacts</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={8.5} lg={8.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Company
                          </MDTypography>
                          <CompanyOptions
                            triggerRefetch={triggerRefetch}
                            selectedCompany={company}
                            setTriggerRefetch={(trigger) => setTriggerRefetch(trigger)}
                          >
                            {({ companyOptions, loading, search, pagination }) => (
                              <Controller
                                name="companyId"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={
                                        companyOptions?.find((x) => x.value === field.value) || null
                                      }
                                      options={companyOptions ?? []}
                                      loading={loading}
                                      label="Company"
                                      search={search}
                                      onLoadMore={pagination.loadMore}
                                      hasMore={pagination.hasNextPage}
                                      onChange={(value) => {
                                        field.onChange(value || null);
                                      }}
                                    />
                                  </>
                                )}
                              />
                            )}
                          </CompanyOptions>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <a ref={downloadFileRef} className="d-none" />
                          <MDButton color="success" type="submit" fullWidth sx={{ height: "40px" }}>
                            RUN REPORT
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            color="info"
                            onClick={handleDownload}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              download
                            </Icon>
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            id="columns-visibility"
                            color="info"
                            onClick={handleMenuOpen}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              visibility
                            </Icon>
                          </MDButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                              style: {
                                maxHeight: 400,
                                minWidth: 250,
                              },
                            }}
                          >
                            {columns.map((column) => (
                              <MenuItem
                                key={column.accessor}
                                onClick={() => handleColumnVisibilityChange(column.accessor)}
                              >
                                <Checkbox
                                  checked={columnVisibility[column.accessor]}
                                  onChange={() => handleColumnVisibilityChange(column.accessor)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <ListItemText primary={column.Header} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs>
                  <MDTypography variant="h6" color="text">
                    Report Results
                  </MDTypography>
                </Grid>
                <Grid item>
                  <MDTypography variant="body2" color="text">
                    {companyName ? `Company: ${companyName}` : "All companies"}
                  </MDTypography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
            </Box>

            <Grid container display="flex">
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%", p: 4 }}>
                  <CircularProgress color="info" />
                </Box>
              ) : (
                <DataTable
                  canSearch={false}
                  table={filteredTableData}
                  showTotalEntries={true}
                  isSorted={true}
                  pagination={pagination}
                  paginationProps={{ variant: "gradient", color: "info" }}
                  entriesPerPageOptions={["25", "50"]}
                />
              )}
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportContactDetails;
